import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slack, Check, Loader2, Settings } from "lucide-react";
import { toast } from "sonner";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export default function SlackConnect() {
  const [slackChannel, setSlackChannel] = useState('#general');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['current-user'],
    queryFn: () => base44.auth.me(),
  });

  useEffect(() => {
    if (user) {
      setSlackChannel(user.slack_channel_id || '#general');
      setNotificationsEnabled(user.slack_notifications_enabled || false);
    }
  }, [user]);

  const updateSettingsMutation = useMutation({
    mutationFn: (data) => base44.auth.updateMe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      toast.success('Slack-innstillinger oppdatert');
    },
  });

  const handleConnect = () => {
    toast.info('Kontakt administrator for å sette opp Slack OAuth');
  };

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate({
      slack_channel_id: slackChannel,
      slack_notifications_enabled: notificationsEnabled,
    });
  };

  return (
    <Card className="bg-white border-0 shadow-sm">
      <div className="h-2 bg-gradient-to-r from-zinc-900 to-zinc-900" />
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-lime-100 to-lime-200 flex items-center justify-center flex-shrink-0">
              <Slack className="w-6 h-6 text-zinc-900" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Slack</h3>
              <p className="text-sm text-slate-600">Få varsler om oppgaver i Slack</p>
            </div>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-start gap-2 text-sm text-slate-600">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            <span>Sanntidsvarsler når oppgaver endrer status</span>
          </div>
          <div className="flex items-start gap-2 text-sm text-slate-600">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            <span>Varsler når du blir tildelt en ny oppgave</span>
          </div>
          <div className="flex items-start gap-2 text-sm text-slate-600">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
            <span>Meldinger når oppgaver fullføres</span>
          </div>
        </div>

        <div className="space-y-4 mb-6 p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Label htmlFor="slack-notifications" className="text-sm font-medium">
                Aktiver Slack-varsler
              </Label>
              <p className="text-xs text-slate-500 mt-1">
                Motta varsler om oppgaveendringer
              </p>
            </div>
            <Switch
              id="slack-notifications"
              checked={notificationsEnabled}
              onCheckedChange={setNotificationsEnabled}
            />
          </div>

          {notificationsEnabled && (
            <div className="space-y-2">
              <Label htmlFor="slack-channel" className="text-sm font-medium">
                Slack-kanal
              </Label>
              <Input
                id="slack-channel"
                value={slackChannel}
                onChange={(e) => setSlackChannel(e.target.value)}
                placeholder="#general"
                className="h-9"
              />
              <p className="text-xs text-slate-500">
                Kanalen hvor varsler skal sendes (f.eks. #general eller @brukernavn)
              </p>
            </div>
          )}

          <Button
            onClick={handleSaveSettings}
            disabled={updateSettingsMutation.isPending}
            className="w-full"
            variant="outline"
          >
            {updateSettingsMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Lagrer...
              </>
            ) : (
              <>
                <Settings className="w-4 h-4 mr-2" />
                Lagre innstillinger
              </>
            )}
          </Button>
        </div>

        <Button
          onClick={handleConnect}
          className="w-full bg-zinc-900 hover:bg-zinc-900"
        >
          <Slack className="w-4 h-4 mr-2" />
          Koble til Slack
        </Button>

        <div className="mt-4 p-3 bg-lime-100 rounded-lg">
          <p className="text-xs text-zinc-900">
            💡 Når tilkoblingen er satt opp, mottar du automatiske varsler i din valgte Slack-kanal.
          </p>
        </div>
      </div>
    </Card>
  );
}